import { formatCPF, titleCase } from '@westhetics/ui'
import { onlyDigits } from '@/features/clinic-profile/doc-number'
import { formatPostalCode } from '@/features/clinic-profile/form'
import { isoToBrDate } from '@/lib/dates'
import type { IdentityDocument, Prefill } from '../identity/api'
import { maskWhatsapp, type PatientFormState } from './form'
import { DEFAULT_DIAL_CODE, splitWhatsapp } from './labels'

/**
 * O preenchimento rápido na recepção (RN-PAC-58, T58): as frases, o documento do formulário e os dados
 * trazidos no formato do formulário. A tela está em `identity-block.tsx`.
 */

export const RECEPTION_MESSAGES = {
  locked: 'O e-mail entra pelo código: este documento já tem cadastro na Westhetics.',
  confirmed: 'Confirmado pelo código. Para trocar, edite a ficha depois de cadastrar.',
  omitted:
    'Cadastro salvo sem o e-mail: este documento já tem cadastro na Westhetics, e o e-mail só entra confirmado pelo código.',
} as const

/** O documento do formulário, como a API recebe; nulo quando não está inteiro. */
export function receptionDocument(values: PatientFormState): IdentityDocument | null {
  if (values.isForeign) {
    const number = values.foreignDocumentNumber.trim().toUpperCase()
    if (!values.foreignDocumentType || !/^[A-Z0-9]{4,20}$/.test(number)) return null
    if (!/^[A-Z]{2}$/.test(values.foreignDocumentCountry)) return null
    return { type: values.foreignDocumentType, number, country: values.foreignDocumentCountry }
  }
  const digits = onlyDigits(values.docNumber)
  return digits.length === 11 ? { type: 'cpf', number: digits } : null
}

/** Os dados pessoais trazidos (RN-PAC-54), no formato do formulário; o e-mail é o confirmado. */
export function prefillPatch(prefill: Prefill): Partial<PatientFormState> {
  const whatsapp = splitWhatsapp(prefill.whatsapp)
  const address = prefill.address
  const country = address?.country ?? 'BR'
  return {
    name: titleCase(prefill.name),
    birthDate: isoToBrDate(prefill.birthDate),
    ...(prefill.docNumber ? { isForeign: false, docNumber: formatCPF(prefill.docNumber) } : {}),
    ...(prefill.foreignDocument
      ? {
          isForeign: !prefill.docNumber,
          foreignDocumentType: prefill.foreignDocument.type,
          foreignDocumentNumber: prefill.foreignDocument.number,
          foreignDocumentCountry: prefill.foreignDocument.country,
        }
      : {}),
    ...(prefill.rg ? { rg: prefill.rg } : {}),
    ...(prefill.maritalStatus ? { maritalStatus: prefill.maritalStatus } : {}),
    ...(prefill.occupation ? { occupation: prefill.occupation } : {}),
    gender: prefill.gender,
    email: prefill.email,
    ...(prefill.whatsapp
      ? {
          dialCode: whatsapp.dialCode || DEFAULT_DIAL_CODE,
          whatsapp: maskWhatsapp(whatsapp.national, whatsapp.dialCode),
        }
      : {}),
    ...(address
      ? {
          country,
          postalCode:
            country === 'BR'
              ? formatPostalCode(address.postalCode ?? '')
              : (address.postalCode ?? ''),
          street: address.street ?? '',
          number: address.number ?? '',
          complement: address.complement ?? '',
          district: address.district ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
        }
      : {}),
  }
}
